import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Card, SectionTitle } from "../ui-bj/Primitives";
import { API } from "../../context/AuthContext";
import { PRODUCTS } from "../../config/products";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const PRODUCT_NAME = Object.fromEntries(PRODUCTS.map((p) => [p.key, p.name]));

const tooltipStyle = {
  background: "#0E1C15",
  border: "1px solid rgba(200,164,106,.45)",
  borderRadius: 0,
  fontFamily: "Hanken Grotesk, sans-serif",
  fontSize: 12,
  color: "#F2EBDD",
};

const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RevenueTooltip = ({ active, payload, label, products }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
      <div style={{ marginBottom: 4 }}>{label} · {money(row.__total)}</div>
      {products.filter((pk) => row[pk] > 0).map((pk) => (
        <div key={pk} style={{ color: "#BDB6A6" }}>{PRODUCT_NAME[pk] || pk}: {money(row[pk])}</div>
      ))}
    </div>
  );
};

const monthLabel = (yyyymm) => {
  const [y, m] = yyyymm.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-US", { month: "short" });
};

/**
 * Monthly revenue stacked by product, sourced from `invoices_v2` payments
 * distributed pro-rata across each invoice's non-pass-through product lines.
 * Empty product bars are collapsed by Recharts automatically.
 */
export const RevenueByProductChart = () => {
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/billing/revenue-by-month?months=6`, tokenCfg())
      .then(({ data }) => {
        if (!alive) return;
        // Recharts wants one row per month with a column per product.
        const rows = data.months.map((mk) => {
          const row = { month: monthLabel(mk), monthKey: mk };
          let total = 0;
          for (const pk of data.products) {
            const cents = data.series[pk]?.[data.months.indexOf(mk)] || 0;
            row[pk] = cents / 100;
            total += cents;
          }
          row.__total = total / 100;
          return row;
        });
        setState({ status: "ok", rows, products: data.products });
      })
      .catch((e) => {
        if (!alive) return;
        setState({ status: "error", detail: e?.response?.data?.detail || e.message });
      });
    return () => { alive = false; };
  }, []);

  const hasAny = state.status === "ok" && state.rows.some((r) => r.__total > 0);

  return (
    <Card data-testid="revenue-by-product-chart">
      <SectionTitle right="Last 6 months">Monthly revenue</SectionTitle>
      <div className="h-80">
        {state.status === "loading" && (
          <div className="h-full flex items-center justify-center text-[#5E4D40] text-xs">Loading…</div>
        )}
        {state.status === "error" && (
          <div className="h-full flex items-center justify-center text-[#5C1A24] text-xs">{state.detail}</div>
        )}
        {state.status === "ok" && !hasAny && (
          <div className="h-full flex flex-col items-center justify-center text-center text-[#5E4D40] text-xs px-6">
            <div className="font-heading text-[#2A1C15] text-base mb-1">No settled revenue yet</div>
            <div className="max-w-xs">Once you charge or record payments on invoices, this chart breaks the revenue down by product month over month.</div>
          </div>
        )}
        {state.status === "ok" && hasAny && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={state.rows} margin={{ top: 10, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(42,28,21,0.18)" vertical={false} />
              <XAxis dataKey="month" stroke="#5E4D40" tick={{ fontFamily: "Hanken Grotesk", fontSize: 11, fill: "#5E4D40" }} axisLine={{ stroke: "rgba(42,28,21,0.18)" }} tickLine={false} />
              <YAxis stroke="#5E4D40" tick={{ fontFamily: "Hanken Grotesk", fontSize: 11, fill: "#5E4D40" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Tooltip cursor={{ fill: "rgba(42,28,21,0.06)" }} content={<RevenueTooltip products={state.products} />} />
              <Bar dataKey="__total" fill="#6E5022" background={{ fill: "rgba(42,28,21,0.08)" }} maxBarSize={48} isAnimationActive />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};
